<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  scheduledAt: { type: Date, default: null },
  forceOpen: { type: Boolean, default: false },
});
const emit = defineEmits(['update', 'clear', 'close']);

const rootRef = ref(null);
const localValue = ref('');

const presets = computed(() => {
  const now = new Date();
  const inHour = new Date(now.getTime() + 60 * 60 * 1000);
  const tomorrow9 = new Date(now);
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);
  const monday9 = new Date(now);
  const daysUntilMonday = (1 - monday9.getDay() + 7) % 7 || 7;
  monday9.setDate(monday9.getDate() + daysUntilMonday);
  monday9.setHours(9, 0, 0, 0);
  return [
    { label: 'In 1 hour', value: inHour },
    { label: 'Tomorrow 9 am', value: tomorrow9 },
    { label: 'Monday 9 am', value: monday9 },
  ];
});

const formatPreset = d =>
  d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const pick = d => emit('update', d);
const submitCustom = () => {
  if (!localValue.value) return;
  const d = new Date(localValue.value);
  if (isNaN(d.getTime()) || d <= new Date()) return;
  pick(d);
};

// Close on outside click — the picker is a transient popover, not a permanent banner.
const handleClickOutside = e => {
  if (rootRef.value && !rootRef.value.contains(e.target)) emit('close');
};
onMounted(() => {
  // Defer so the same click that opened us doesn't immediately close us.
  setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
});
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));
</script>

<template>
  <div
    ref="rootRef"
    class="my-1 mx-1 p-2 rounded-lg shadow-md border min-w-[260px] max-w-[300px] self-end"
    :style="{
      background: 'var(--bloom-bg-primary)',
      borderColor: 'var(--bloom-border-default)',
    }"
  >
    <div
      class="text-[11px] font-medium px-1 pb-1.5 mb-1 border-b flex items-center justify-between"
      :style="{
        color: 'var(--bloom-text-secondary)',
        borderColor: 'var(--bloom-border-subtle)',
      }"
    >
      <span>Schedule send</span>
      <button
        v-if="scheduledAt"
        type="button"
        class="text-[10px] opacity-70 hover:opacity-100"
        :style="{ color: 'var(--bloom-text-tertiary)' }"
        @click="$emit('clear')"
      >
        clear
      </button>
    </div>
    <div class="space-y-0.5">
      <button
        v-for="p in presets"
        :key="p.label"
        type="button"
        class="w-full text-left text-xs px-2 py-1.5 rounded transition-colors flex items-baseline justify-between gap-2"
        :class="'hover:bg-n-alpha-2'"
        :style="{ color: 'var(--bloom-text-primary)' }"
        @click="pick(p.value)"
      >
        <span class="font-medium">{{ p.label }}</span>
        <span class="opacity-60 text-[10px]">{{ formatPreset(p.value) }}</span>
      </button>
    </div>
    <div
      class="mt-1.5 pt-1.5 border-t flex items-center gap-1"
      :style="{ borderColor: 'var(--bloom-border-subtle)' }"
    >
      <input
        v-model="localValue"
        type="datetime-local"
        class="flex-1 text-xs px-2 py-1 rounded border"
        :style="{ borderColor: 'var(--bloom-border-default)' }"
      />
      <button
        type="button"
        class="text-xs px-2.5 py-1 rounded font-medium"
        :style="{
          background: 'var(--bloom-purple-500)',
          color: 'var(--bloom-text-inverse)',
        }"
        @click="submitCustom"
      >
        Set
      </button>
    </div>
  </div>
</template>
